/**
 * Installer-facing Support page — installers list & raise their own tickets.
 *
 * Uses the shared NewSupportTicketModal (isStaff=false so priority selector
 * is hidden — installers submit at 'standard' and staff bump if needed).
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, LifeBuoy, Plus, Clock, Flame, Zap, CheckCircle2, MessageCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import NewSupportTicketModal from '@/components/support/NewSupportTicketModal';

const STATUS_META = {
  new:                { label: 'New',                icon: Zap,           chip: 'bg-slate-100 text-slate-700' },
  awaiting_customer:  { label: 'Waiting on you',     icon: MessageCircle, chip: 'bg-sky-100 text-sky-800' },
  awaiting_ox:        { label: 'Waiting on Ox',      icon: AlertTriangle, chip: 'bg-amber-100 text-amber-800' },
  complete:           { label: 'Resolved',           icon: CheckCircle2,  chip: 'bg-emerald-100 text-emerald-800' },
};
const PRIORITY_META = {
  urgent:   { icon: Flame, chip: 'bg-rose-100 text-rose-800' },
  priority: { icon: Zap,   chip: 'bg-amber-100 text-amber-800' },
  standard: { icon: Clock, chip: 'bg-slate-100 text-slate-700' },
};
const CATEGORY_LABEL = {
  delivery: 'Delivery',
  installation_support: 'Installation Support',
  training_course: 'Training Course',
};

export default function MySupportTickets() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [searchParams] = useSearchParams();
  useEffect(() => { if (searchParams.get('new') === '1') setShowNew(true); }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Load failed');
      setRows(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #334155)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            data-testid="my-support-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Home"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <LifeBuoy className="w-5 h-5 shrink-0" /> Support
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              Raise a query with the Ox Resin team — we'll come back to you as soon as possible.
            </p>
          </div>
          <Button
            onClick={() => setShowNew(true)}
            size="sm"
            className="bg-white text-slate-900 hover:bg-slate-100 shrink-0"
            data-testid="my-support-new"
          >
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Request Support</span>
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center" data-testid="my-support-empty">
            <LifeBuoy className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h3 className="font-bold text-slate-900 mb-1">No support requests yet</h3>
            <p className="text-slate-500 text-sm mb-4">Got a problem? Request support and the Ox Resin team will get back to you.</p>
            <Button onClick={() => setShowNew(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-1" /> Request Support
            </Button>
          </Card>
        ) : (
          rows.map((t) => {
            const s = STATUS_META[t.status] || STATUS_META.new;
            const p = PRIORITY_META[t.priority] || PRIORITY_META.standard;
            const SIcon = s.icon;
            const PIcon = p.icon;
            return (
              <Card
                key={t.id}
                onClick={() => navigate(`/support/${t.id}`)}
                className="p-4 cursor-pointer hover:shadow-md transition"
                data-testid={`my-support-row-${t.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.chip}`}>
                    <SIcon className="w-3 h-3" /> {s.label}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.chip}`}>
                    <PIcon className="w-3 h-3" /> {t.priority}
                  </span>
                  {t.category && CATEGORY_LABEL[t.category] && (
                    <span className="inline-flex items-center text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-900 text-white">
                      {CATEGORY_LABEL[t.category]}
                    </span>
                  )}
                  <span className="font-mono text-[11px] text-slate-400 ml-auto">{t.reference}</span>
                </div>
                <div className="mt-1.5 font-semibold text-sm text-slate-900 truncate">{t.title || 'Support'}</div>
                <div className="text-sm text-slate-700 line-clamp-2">{t.description}</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Raised {new Date(t.created_at).toLocaleDateString('en-GB')}
                  {(t.attachments?.length || 0) > 0 && <> · 📎 {t.attachments.length}</>}
                </div>
              </Card>
            );
          })
        )}
      </div>

      {showNew && (
        <NewSupportTicketModal
          isStaff={false}
          initialTitle={searchParams.get('title') || ''}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}
